import React, { useState, useEffect } from 'react';
import { FaGithub, FaExternalLinkAlt, FaCode, FaFire, FaChartLine } from 'react-icons/fa';
import config from '../config';
import './Experience.css';

const LEETCODE_USERNAME = 'MALIPEDDI_SEKHAR';

function Experience() {
  const [leetcodeStats, setLeetcodeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeJourneyIndex, setActiveJourneyIndex] = useState(0);
  const [flowSpinClass, setFlowSpinClass] = useState('');
  const [journeyPulseClass, setJourneyPulseClass] = useState('journey-pulse-a');

  const handleJourneyNodeClick = (index) => {
    setActiveJourneyIndex(index);
    setFlowSpinClass((prev) => (prev === 'flow-spin-a' ? 'flow-spin-b' : 'flow-spin-a'));
    setJourneyPulseClass((prev) => (prev === 'journey-pulse-a' ? 'journey-pulse-b' : 'journey-pulse-a'));
  };

  const journeyFlow = [
    {
      phase: 'Foundation Phase',
      year: '2021 - 2024',
      title: 'Diploma in Computer Science',
      summary: 'Built strong programming fundamentals and started my coding journey with Java and Python while understanding how software works from the ground up.',
      tech: ['Java', 'Python', 'Programming Fundamentals', 'Computer Science Basics']
    },
    {
      phase: 'Problem Solving Phase',
      year: '2024',
      title: 'DSA and Developer Workflow',
      summary: 'Focused on data structures, algorithms, and clean problem-solving patterns while practicing version control and collaborative coding workflows.',
      tech: ['DSA', 'OOP', 'Git', 'GitHub', 'Debugging']
    },
    {
      phase: 'AI Learning Phase',
      year: '2024 - 2025',
      title: 'AI and ML Exploration',
      summary: 'Expanded into artificial intelligence by learning machine learning pipelines, neural networks, deep learning basics, and data-driven thinking.',
      tech: ['AI', 'ML', 'AIML', 'Neural Networks', 'Deep Learning', 'Data Analysis']
    },
    {
      phase: 'Full Stack Phase',
      year: '2025',
      title: 'Java Full Stack Development',
      summary: 'Started building complete applications using Spring Boot on backend and React on frontend with MySQL databases and robust REST APIs.',
      tech: ['Java Full Stack', 'Spring Boot', 'React.js', 'MySQL', 'REST APIs', 'Authentication']
    },
    {
      phase: 'Build and Scale Phase',
      year: '2025 - Present',
      title: 'Production Mindset and Impact',
      summary: 'Now focused on building scalable, practical, and user-centric products with strong architecture, performance, and real-world usability.',
      tech: ['System Design', 'UI/UX', 'Performance', 'Testing', 'Deployment', 'Team Collaboration']
    }
  ];

  // Fallback stats - update these manually when your actual stats change
  const fallbackStats = {
    totalSolved: 50,
    easySolved: 25,
    mediumSolved: 20,
    hardSolved: 5,
    ranking: 0
  };

  useEffect(() => {
    const fetchLeetCodeStats = async () => {
      try {
        const response = await fetch(`${config.API_URL}/api/leetcode/${encodeURIComponent(LEETCODE_USERNAME)}`, {
          method: 'GET',
          headers: { Accept: 'application/json' }
        });

        if (response.ok) {
          const data = await response.json();
          setLeetcodeStats({
            totalSolved: data.totalSolved || data.solvedProblem || fallbackStats.totalSolved,
            easySolved: data.easySolved || fallbackStats.easySolved,
            mediumSolved: data.mediumSolved || fallbackStats.mediumSolved,
            hardSolved: data.hardSolved || fallbackStats.hardSolved,
            ranking: data.ranking || 0
          });
        } else {
          setLeetcodeStats(fallbackStats);
        }
      } catch (error) {
        setLeetcodeStats(fallbackStats);
      } finally {
        setLoading(false);
      }
    };

    fetchLeetCodeStats();
  }, []);

  const experiences = [
    {
      role: 'React JS Developer Intern',
      company: 'UpToSkills',
      period: 'Jan 2026 - July 2026',
      current: true,
      description: [
        'Gained hands-on experience in developing modern web applications using React.js with industry best practices',
        'Built and deployed the UpToSkills platform application with responsive UI and seamless user experience',
        'Developed AI Resume Builder application using React.js, integrating AI-powered features for smart resume generation',
        'Collaborated with cross-functional teams in agile sprints, participating in code reviews and daily standups',
        'Implemented state management using Redux/Context API and optimized application performance'
      ],
      projects: [
        {
          name: 'AI Resume Builder',
          github: 'https://github.com/malipeddisekhar/AI-Resume-Builder'
        }
      ]
    },
    {
      role: 'Java Full Stack Developer Intern',
      company: 'Bytexl',
      period: '2024 - 2025',
      current: false,
      description: [
        'Developed and deployed scalable full-stack web applications using Spring Boot backend and React.js frontend with MySQL database integration',
        'Built Campus Connect - a comprehensive college management platform serving students and faculty with role-based access control',
        'Designed and implemented RESTful APIs following industry best practices, focusing on performance optimization and secure authentication',
        'Collaborated in agile development environment using Git/GitHub for version control and code reviews',
        'Applied software engineering principles including clean code practices, debugging, testing, and comprehensive documentation'
      ],
      projects: [
        {
          name: 'Campus Connect',
          github: 'https://github.com/malipeddisekhar/CampusConnect'
        }
      ]
    }
  ];

  const projects = [
    {
      name: 'Campus Connect – College Management System',
      tech: 'Spring Boot | React.js | MySQL',
      highlights: [
        'Built comprehensive academic management platform serving 1000+ students and faculty with role-based access control',
        'Architected backend with Spring Boot implementing 15+ REST API endpoints with JWT authentication',
        'Designed normalized MySQL database schema ensuring data integrity and ACID compliance',
        'Developed features: student registration, automated attendance tracking, dynamic timetable management, result publishing',
        'Created responsive React frontend with modular component architecture and Redux state management'
      ]
    },
    {
      name: 'Student Performance Prediction System',
      tech: 'Python | Scikit-learn | Pandas | Matplotlib',
      highlights: [
        'Developed ML solution achieving 85% accuracy in predicting student academic performance',
        'Implemented data preprocessing pipeline handling 500+ student records with feature engineering',
        'Trained and evaluated multiple regression models (Linear Regression, Random Forest, Gradient Boosting)',
        'Built interactive visualization dashboard using Matplotlib and Seaborn for academic analytics'
      ]
    }
  ];

  return (
    <section className="experience section">
      <div className="container">
        {/* Journey Flow */}
        <div className="journey-flow-section">
          <h2 className="section-title">Journey Flow</h2>
          <p className="journey-flow-intro journey-flow-quote">
            "I did not wait for opportunities - I engineered them. From diploma roots to full stack and AI systems, every stage of my journey turned learning into real impact."
          </p>
          <div className={`journey-flow-track ${flowSpinClass}`}>
            {journeyFlow.map((step, index) => (
              <article
                key={step.title}
                className={`journey-node ${index % 2 === 0 ? 'left' : 'right'}`}
                style={{ '--node-index': index }}
              >
                <span className="journey-node-dot" aria-hidden="true"></span>
                <div
                  className={`journey-node-card card ${activeJourneyIndex === index ? `active ${journeyPulseClass}` : ''}`}
                  onClick={() => handleJourneyNodeClick(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleJourneyNodeClick(index);
                    }
                  }}
                >
                  <div className="journey-node-meta">
                    <span className="journey-phase">{step.phase}</span>
                    <span className="journey-year">{step.year}</span>
                  </div>
                  <h3 className="journey-title">{step.title}</h3>
                  <p className="journey-summary">{step.summary}</p>
                  <div className="journey-tech-stack">
                    {step.tech.map((item) => (
                      <span key={item} className="journey-tech-tag">{item}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Professional Experience */}
        <h2 className="section-title">Professional Experience</h2>
        <div className="exp-cards">
          {experiences.map((exp, index) => (
            <div key={index} className={`exp-card card ${exp.current ? 'current-exp' : ''}`}>
              <div className="exp-header">
                <div>
                  <h3 className="exp-role">{exp.role}</h3>
                  <p className="exp-company">{exp.company}</p>
                </div>
                <div className="exp-period-wrapper">
                  {exp.current && <span className="current-badge">Current</span>}
                  <span className="exp-period">{exp.period}</span>
                </div>
              </div>
              <ul className="exp-list">
                {exp.description.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              {exp.projects && exp.projects.length > 0 && (
                <div className="exp-projects">
                  <h4>Key Projects:</h4>
                  <div className="exp-project-links">
                    {exp.projects.map((proj, i) => (
                      <a 
                        key={i} 
                        href={proj.github} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="exp-project-link"
                      >
                        <FaGithub /> {proj.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* LeetCode Stats */}
        <div className="leetcode-section">
          <h2 className="section-title">
            <FaCode style={{ marginRight: '10px', color: '#00d4ff' }} />
            LeetCode Progress
          </h2>
          <div className="leetcode-card card">
            {loading ? (
              <div className="leetcode-loading">
                <div className="loading-spinner"></div>
                <p>Loading LeetCode stats...</p>
              </div>
            ) : leetcodeStats ? (
              <>
                <div className="leetcode-header">
                  <div className="leetcode-profile">
                    <div className="leetcode-logo">
                      <FaCode />
                    </div>
                    <div>
                      <h3>{LEETCODE_USERNAME}</h3>
                      <a 
                        href={`https://leetcode.com/u/${LEETCODE_USERNAME}/`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="leetcode-link"
                      >
                        View Profile <FaExternalLinkAlt />
                      </a>
                    </div>
                  </div>
                  {leetcodeStats.ranking > 0 && (
                    <div className="leetcode-rank">
                      <FaChartLine />
                      <span>Rank: {leetcodeStats.ranking.toLocaleString()}</span>
                    </div>
                  )}
                </div>

                <div className="leetcode-stats-grid">
                  <div className="leetcode-stat-card total">
                    <div className="stat-number">{leetcodeStats.totalSolved || 0}</div>
                    <div className="stat-label">Problems Solved</div>
                    <div className="stat-icon"><FaFire /></div>
                  </div>
                  
                  <div className="leetcode-stat-card easy">
                    <div className="stat-number">{leetcodeStats.easySolved || 0}</div>
                    <div className="stat-label">Easy</div>
                    <div className="stat-bar">
                      <div className="stat-fill" style={{ width: `${leetcodeStats.easySolved ? (leetcodeStats.easySolved / 830) * 100 : 0}%` }}></div>
                    </div>
                  </div>
                  
                  <div className="leetcode-stat-card medium">
                    <div className="stat-number">{leetcodeStats.mediumSolved || 0}</div>
                    <div className="stat-label">Medium</div>
                    <div className="stat-bar">
                      <div className="stat-fill" style={{ width: `${leetcodeStats.mediumSolved ? (leetcodeStats.mediumSolved / 1742) * 100 : 0}%` }}></div>
                    </div>
                  </div>
                  
                  <div className="leetcode-stat-card hard">
                    <div className="stat-number">{leetcodeStats.hardSolved || 0}</div>
                    <div className="stat-label">Hard</div>
                    <div className="stat-bar">
                      <div className="stat-fill" style={{ width: `${leetcodeStats.hardSolved ? (leetcodeStats.hardSolved / 756) * 100 : 0}%` }}></div>
                    </div>
                  </div>
                </div>

                <div className="leetcode-footer">
                  <p><span className="quote-icon">💡</span> "Code is like humor. When you have to explain it, it's bad." — Cory House</p>
                </div>
              </>
            ) : (
              <div className="leetcode-error">
                <p>Unable to load LeetCode stats. <a href={`https://leetcode.com/u/${LEETCODE_USERNAME}/`} target="_blank" rel="noopener noreferrer">Visit Profile</a></p>
              </div>
            )}
          </div>
        </div>

        {/* Featured Projects */}
        <div className="projects-section">
          <h2 className="section-title">Featured Projects</h2>
          <div className="projects-grid">
            {projects.map((project, index) => (
              <div key={index} className="project-card card">
                <h3 className="project-name">{project.name}</h3>
                <p className="project-tech">{project.tech}</p>
                <ul className="project-highlights">
                  {project.highlights.map((highlight, i) => (
                    <li key={i}>{highlight}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Experience;
